import { SKYSPECS_ORG } from 'utils/constants';
import { storageManager } from 'utils/storage';

/** Base key; org isolation is via `${base}:${customerId}` (same pattern as ColumnPicker). */
export const DAMAGES_2_NO_PRE_LOADING_STORAGE_KEY = 'damages-2-no-pre-loading';

const damages2NoPreLoadingStorage = (customerId: string) =>
  storageManager({ storageKey: `${DAMAGES_2_NO_PRE_LOADING_STORAGE_KEY}:${customerId}` });

/** Returns null when this org has no stored preference. */
export const getStoredDamages2PreLoadingPreference = (
  customerId?: string | null
): boolean | null => {
  if (!customerId) {
    return null;
  }

  const stored = damages2NoPreLoadingStorage(customerId).get();
  return typeof stored === 'boolean' ? stored : null;
};

/** Writes this org's preference; other orgs use different keys so they are untouched. */
export const setStoredDamages2PreLoadingPreference = (
  customerId: string,
  enabled: boolean
): void => {
  damages2NoPreLoadingStorage(customerId).set(enabled);
};

/**
 * Effective defer-until-filtered for non-SkySpecs orgs:
 * stored user preference if set, otherwise the org feature-flag default.
 *
 * `true` means defer the Damages 2.0 query (No Pre-Loading). Matches
 * DAMAGES_2_NO_PRE_LOADING: flag on ⇒ default defer.
 */
export const resolveShouldDeferDamages2Query = ({
  orgFeatureFlagEnabled,
  storedPreference,
}: {
  orgFeatureFlagEnabled: boolean;
  storedPreference: boolean | null;
}): boolean => storedPreference ?? orgFeatureFlagEnabled;

/**
 * When true, Damages 2.0 asks DataTable to defer the data query until filtered.
 *
 * SkySpecs keeps the hardcoded prod/prod-review behavior and ignores the user toggle.
 * Other orgs defer when No Pre-Loading is on (stored preference, else the org flag default).
 *
 * Env asymmetry (intentional): SkySpecs only defers in prod/prod-review so local/stage
 * keep an unfiltered table for debugging. Non-SkySpecs orgs follow the setting in every
 * environment — including local — so empty tables there usually mean No Pre-Loading is on.
 *
 * Column-filter / banner gating lives in DataTable (`deferQueryUntilFiltered`).
 * Callers still short-circuit when Damages-specific universal scope filters are present
 * (site/turbine pages) before passing this boolean.
 */
export const shouldDeferDamages2Query = ({
  customerId,
  isProdLikeEnv,
  shouldDeferUntilFiltered,
}: {
  customerId?: string | null;
  isProdLikeEnv: boolean;
  shouldDeferUntilFiltered: boolean;
}): boolean => {
  // SkySpecs: prod-like only (see env asymmetry note above).
  if (customerId === SKYSPECS_ORG) {
    return isProdLikeEnv;
  }

  return shouldDeferUntilFiltered;
};
